import { useTranslation, Trans } from "next-i18next";
import Image from "next/image";
import { PageQuery } from "gql/graphql";

import Button from "components/Button/Button";
import CheckedListItem from "components/ListItem/CheckedListItem";
import GooglePodcasts from "components/Logos/GooglePodcasts";
import PremiumButton from "components/Button/PremiumButton";
import RadioGroup, { RadioGroupItem } from "components/RadioGroup/RadioGroup";
import TooltipOnHover from "components/Tooltip/TooltipOnHover";

import ArrowRight from "public/assets/arrow-right.svg";
import Lock from "public/assets/lock.svg";
import Stripe from "public/assets/stripe.svg";
import EpisodeListItem from "./ListItem/EpisodeListItem";

export default function ProductDetailsSingle({
  data,
  selectedPaymentOption,
  setSelectedPaymentOption,
  setIsSignupDialogOpen,
  setIsSigninDialogOpen,
}: {
  data: PageQuery;
  productName: string;
  trialDays: number;
  selectedPaymentOption: string;
  setSelectedPaymentOption: (value: string | number | object) => void;
  setIsSignupDialogOpen: (value: boolean) => void;
  setIsSigninDialogOpen: (value: boolean) => void;
  slug: string;
}) {
  const { t } = useTranslation("common");

  return (
    <>
      <p className="text-white/70">
        <Trans i18nKey="purchase_subheader">
          Get episodes of <span className="text-white/90">{data?.page.hero.title}</span> for only{" "}
          {data?.page.product.purchaseOptions[0].price.displayPrice}.
        </Trans>
      </p>
      <ul className="mt-6 list-none space-y-4 font-medium lg:mt-8">
        {data?.page?.product?.features?.map((checkedListItem, index) => (
          <>
            {checkedListItem.type === "text" && (
              <CheckedListItem key={index}>{checkedListItem.content}</CheckedListItem>
            )}

            {checkedListItem.type === "episode" && (
              <EpisodeListItem
                key={index}
                episode={JSON.parse(checkedListItem.data)}
              ></EpisodeListItem>
            )}

            {checkedListItem.type === "localized_key" && (
              <CheckedListItem>
                <span className="flex items-center">
                  {/* <Trans i18nKey="listen on"> */}
                  Listen on <span className="sr-only">Spotify</span>
                  &nbsp;{" "}
                  <TooltipOnHover text={t("spotify")} className="h-6 w-6">
                    <Image
                      unoptimized={true}
                      alt="spotify"
                      loading="eager"
                      src="/assets/app-spotify-colorized.png"
                      width="24"
                      height="24"
                      className="inline-block transition hover:scale-90"
                      aria-hidden="true"
                      quality={100}
                    />
                  </TooltipOnHover>
                  ,<span className="sr-only">Apple Podcasts</span>
                  &nbsp;{" "}
                  <TooltipOnHover text={t("apple_podcasts")} className="h-6 w-6">
                    <Image
                      alt="apple podcasts"
                      unoptimized={true}
                      loading="eager"
                      src="/assets/app-apple-podcast-colorized.png"
                      width="24"
                      height="24"
                      className="inline-block transition hover:scale-90"
                      aria-hidden="true"
                      quality={100}
                    />
                  </TooltipOnHover>
                  , and
                  <span className="sr-only">Google Podcasts</span>
                  &nbsp;
                  <GooglePodcasts className="h-6 w-6" />
                  {/* </Trans> */}
                </span>
              </CheckedListItem>
            )}
          </>
        ))}
      </ul>
      <section className="mt-8">
        <RadioGroup
          name={"select-plan"}
          defaultValue={selectedPaymentOption}
          onChange={setSelectedPaymentOption}
          label={""}
        >
          {data?.page.product.purchaseOptions.map((option) => (
            <RadioGroupItem key={option.identifier} value={option.identifier}>
              <span className="">{t(option.name)}</span>
              <span className="ml-auto text-white/50 transition group-hover:!text-white ui-checked:text-white/90">
                {`${t("displayPrice", {
                  price: option.price.price,
                })} ${option.price.currency === "SEK" ? "kr" : option.price.currency} `}
              </span>
            </RadioGroupItem>
          ))}
        </RadioGroup>
        <div className="mt-8">
          <PremiumButton
            fullWidth
            size="large"
            importance="premium"
            onClick={() => setIsSignupDialogOpen(true)}
          >
            {t("purchase_header")}
            <ArrowRight className="ml-2 fill-white" />
          </PremiumButton>
        </div>
      </section>
      <div className="mt-8">
        <p className="text-center text-xs text-white/70">
          <Lock title="secure payments" className="mr-2 inline-block fill-white/70" />
          {t("secure_payments")}
          <Stripe title="stripe" className="ml-[6px] inline-block" />
        </p>
        <div className="mt-10 flex justify-center">
          <Button
            className="w-full lg:mx-auto lg:w-auto"
            size="small"
            importance="secondary"
            onClick={() => setIsSigninDialogOpen(true)}
          >
            {t("sign_in_button")}
          </Button>
        </div>
      </div>
    </>
  );
}
